import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { ArrowLeft, Building2, Users, Briefcase, Clock, Loader2, Copy, Eye, EyeOff, Phone, Mail, Globe, MapPin, Pencil, Save, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";
import { JOB_STATUS_COLORS, PARTNER_TYPE_LABELS } from "@/lib/constants";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminParceiroDetalhe() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<any>(null);

  const { data: org, isLoading: orgLoading } = useQuery({
    queryKey: ["admin_org_detail", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_orgs")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: users } = useQuery({
    queryKey: ["admin_org_users", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_users")
        .select("*, profiles(full_name, phone)")
        .eq("org_id", id!);
      if (error) throw error;
      return data as any[];
    },
  });

  const { data: jobs } = useQuery({
    queryKey: ["admin_org_jobs", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_posts")
        .select("id, title, status, created_at")
        .eq("org_id", id!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: recentLogs } = useQuery({
    queryKey: ["admin_org_audit", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_audit_logs")
        .select("action, entity_type, created_at")
        .eq("org_id", id!)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: any) => {
      const { error } = await supabase
        .from("partner_orgs")
        .update(updates)
        .eq("id", id!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_org_detail", id] });
      setEditing(false);
      toast({ title: "Dados atualizados!" });
    },
    onError: (err: any) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      // Delete in FK order
      await supabase.from("partner_audit_logs").delete().eq("org_id", id!);
      await supabase.from("contact_requests").delete().eq("org_id", id!);
      
      // Delete shortlist items then shortlists
      const { data: shortlists } = await supabase.from("shortlists").select("id").eq("org_id", id!);
      if (shortlists && shortlists.length > 0) {
        const slIds = shortlists.map(s => s.id);
        await supabase.from("shortlist_items").delete().in("shortlist_id", slIds);
        await supabase.from("shortlists").delete().eq("org_id", id!);
      }

      // Delete job applications events, applications, then jobs
      const { data: jobs } = await supabase.from("job_posts").select("id").eq("org_id", id!);
      if (jobs && jobs.length > 0) {
        const jobIds = jobs.map(j => j.id);
        const { data: apps } = await supabase.from("job_applications").select("id").in("job_id", jobIds);
        if (apps && apps.length > 0) {
          const appIds = apps.map(a => a.id);
          await supabase.from("application_status_events").delete().in("application_id", appIds);
          await supabase.from("job_applications").delete().in("job_id", jobIds);
        }
        await supabase.from("job_posts").delete().eq("org_id", id!);
      }

      await supabase.from("partner_users").delete().eq("org_id", id!);
      
      const { error } = await supabase.from("partner_orgs").delete().eq("id", id!);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Parceria cancelada", description: "Todos os dados do parceiro foram removidos." });
      navigate("/admin/parceiros");
    },
    onError: (err: any) => {
      toast({ title: "Erro ao cancelar", description: err.message, variant: "destructive" });
    },
  });

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: `${label} copiado!` });
  };

  if (orgLoading) {
    return <div className="p-8 flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (!org) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Parceiro não encontrado.</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate("/admin/parceiros")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
      </div>
    );
  }

  const startEdit = () => {
    setEditData({
      name: org.name || "",
      industry: org.industry || "",
      contact_name: org.contact_name || "",
      contact_email: org.contact_email || "",
      contact_phone: org.contact_phone || "",
      website: org.website || "",
      city: org.city || "",
      state: org.state || "",
    });
    setEditing(true);
  };

  const saveEdit = () => {
    updateMutation.mutate(editData);
  };

  const STATUS_COLORS = JOB_STATUS_COLORS;

  const partnerTypes = Array.isArray(org.partner_types) ? org.partner_types : [];

  return (
    <div className="p-8 max-w-4xl">
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate("/admin/parceiros")}>
        <ArrowLeft className="w-4 h-4 mr-1" /> Parceiros
      </Button>

      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-accent" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">{org.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              {org.industry && <Badge variant="outline">{org.industry}</Badge>}
              <span className="text-xs text-muted-foreground">Desde {new Date(org.created_at).toLocaleDateString("pt-BR")}</span>
            </div>
          </div>
        </div>
        {!editing && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={startEdit}>
              <Pencil className="w-4 h-4 mr-1" /> Editar
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10">
                  <Trash2 className="w-4 h-4 mr-1" /> Cancelar Parceria
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancelar parceria com {org.name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação é irreversível. Todos os dados da organização serão excluídos permanentemente, incluindo vagas, candidaturas, shortlists e logs de auditoria.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Voltar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteMutation.mutate()}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Excluindo...</>
                    ) : (
                      "Sim, cancelar parceria"
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </motion.div>

      {/* Credentials */}
      {org.temp_password && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 bg-card rounded-2xl p-5 border border-border">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            🔑 Credenciais de Acesso
          </h2>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground w-16">E-mail:</span>
              <code className="text-sm font-mono flex-1">{org.contact_email}</code>
              <Button size="icon" variant="ghost" onClick={() => copy(org.contact_email!, "E-mail")}><Copy className="w-4 h-4" /></Button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground w-16">Senha:</span>
              <code className="text-sm font-mono flex-1">{showPassword ? org.temp_password : "••••••••••••"}</code>
              <Button size="icon" variant="ghost" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              <Button size="icon" variant="ghost" onClick={() => copy(org.temp_password!, "Senha")}><Copy className="w-4 h-4" /></Button>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => {
                const msg = `Olá! Sua conta no Corre Digital foi criada.\n\nAcesse: ${window.location.origin}/empresa/login\nE-mail: ${org.contact_email}\nSenha: ${org.temp_password}\n\nRecomendamos trocar a senha no primeiro acesso.`;
                copy(msg, "Mensagem completa");
              }}
            >
              <Copy className="w-4 h-4 mr-1" /> Copiar mensagem completa
            </Button>
          </div>
        </motion.div>
      )}

      {/* Company Info */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mb-6 bg-card rounded-2xl p-5 border border-border">
        <h2 className="font-heading text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <Building2 className="w-5 h-5" /> Informações da Empresa
        </h2>

        {editing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">Nome</label><Input value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Segmento</label><Input value={editData.industry} onChange={e => setEditData({ ...editData, industry: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Contato</label><Input value={editData.contact_name} onChange={e => setEditData({ ...editData, contact_name: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">E-mail</label><Input value={editData.contact_email} onChange={e => setEditData({ ...editData, contact_email: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Telefone</label><Input value={editData.contact_phone} onChange={e => setEditData({ ...editData, contact_phone: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Website</label><Input value={editData.website} onChange={e => setEditData({ ...editData, website: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Cidade</label><Input value={editData.city} onChange={e => setEditData({ ...editData, city: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Estado</label><Input value={editData.state} onChange={e => setEditData({ ...editData, state: e.target.value })} /></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={saveEdit} disabled={updateMutation.isPending}>
                <Save className="w-4 h-4 mr-1" /> Salvar
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                <X className="w-4 h-4 mr-1" /> Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {org.contact_name && (
              <div className="flex items-center gap-2 text-muted-foreground"><Users className="w-3.5 h-3.5" /> {org.contact_name}</div>
            )}
            {org.contact_email && (
              <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-3.5 h-3.5" /> {org.contact_email}</div>
            )}
            {org.contact_phone && (
              <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-3.5 h-3.5" /> {org.contact_phone}</div>
            )}
            {org.website && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Globe className="w-3.5 h-3.5" />
                <a href={org.website} target="_blank" rel="noopener noreferrer" className="underline truncate">{org.website}</a>
              </div>
            )}
            {(org.city || org.state) && (
              <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="w-3.5 h-3.5" /> {[org.city, org.state].filter(Boolean).join(", ")}</div>
            )}
            {org.industry && (
              <div className="flex items-center gap-2 text-muted-foreground"><Building2 className="w-3.5 h-3.5" /> {org.industry}</div>
            )}
          </div>
        )}

        {partnerTypes.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {partnerTypes.map((t: string) => (
              <Badge key={t} variant="outline" className="text-xs">{PARTNER_TYPE_LABELS[t] ?? t}</Badge>
            ))}
          </div>
        )}

        {org.message && (
          <div className="bg-muted/50 rounded-xl p-3 text-sm text-foreground mt-3 italic">"{org.message}"</div>
        )}
      </motion.div>

      {/* Users */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
        <h2 className="font-heading text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <Users className="w-5 h-5" /> Usuários ({users?.length ?? 0})
        </h2>
        <div className="space-y-2">
          {(users ?? []).map((u: any) => (
            <div key={u.user_id} className="bg-card rounded-xl p-4 border border-border flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">{u.profiles?.full_name ?? "Sem nome"}</p>
                <p className="text-xs text-muted-foreground">{u.partner_role} · {u.profiles?.phone ?? "Sem telefone"}</p>
              </div>
              <Badge variant="outline" className="text-xs">{u.partner_role}</Badge>
            </div>
          ))}
          {(users ?? []).length === 0 && <p className="text-sm text-muted-foreground">Nenhum usuário vinculado.</p>}
        </div>
      </motion.div>

      {/* Jobs */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-6">
        <h2 className="font-heading text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <Briefcase className="w-5 h-5" /> Vagas ({jobs?.length ?? 0})
        </h2>
        <div className="space-y-2">
          {(jobs ?? []).map((j) => (
            <div key={j.id} className="bg-card rounded-xl p-4 border border-border flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">{j.title}</p>
                <p className="text-xs text-muted-foreground">{new Date(j.created_at).toLocaleDateString("pt-BR")}</p>
              </div>
              <Badge className={STATUS_COLORS[j.status] ?? "bg-muted text-muted-foreground"}>{j.status}</Badge>
            </div>
          ))}
          {(jobs ?? []).length === 0 && <p className="text-sm text-muted-foreground">Nenhuma vaga publicada.</p>}
        </div>
      </motion.div>

      {/* Recent Audit */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <h2 className="font-heading text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <Clock className="w-5 h-5" /> Atividade Recente
        </h2>
        <div className="space-y-1">
          {(recentLogs ?? []).map((log, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <span className="text-sm text-foreground">{log.action}</span>
              <span className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleDateString("pt-BR")}</span>
            </div>
          ))}
          {(recentLogs ?? []).length === 0 && <p className="text-sm text-muted-foreground">Nenhuma atividade registrada.</p>}
        </div>
      </motion.div>
    </div>
  );
}
